/******************************************************************************
==============================================================================
BabiGO

LegacyRepository

Repository de compatibilité.

Il centralise la lecture des anciennes structures Firestore
stockées sous forme de Map dans les documents utilisateurs.

A supprimer lorsque la migration sera terminée.

==============================================================================
*/

import {

    doc,

    getDoc,

} from "firebase/firestore";

import {

    db,

} from "../firebase/firebase";

import { DATA_SOURCE } from '../../components/constants/app';
import { LEGACY_FIELDS } from '../../components/constants/legacyFields';

class LegacyRepository {

    /* =======================================================================
       DOCUMENT UTILISATEUR
    ======================================================================= */

    async getUserDocument(userId) {

        if (!userId) {

            return null;

        }

        const snapshot = await getDoc(

            doc(

                db,

                "users",

                userId

            )

        );

        if (!snapshot.exists()) {

            return null;

        }

        return snapshot.data();

    }

        /* =======================================================================
       NORMALISATION D'UNE MAP FIRESTORE
       Convertit une Map en tableau exploitable.
    ======================================================================= */

    normalizeMap(map = {}, source = DATA_SOURCE.LEGACY) {

        if (

            !map ||

            typeof map !== "object"

        ) {

            return [];

        }

        return Object.entries(map).map(

            ([id, value]) => ({

                id,

                source,

                ...value,

            })

        );

    }

    /* =======================================================================
       TRI PAR DATE
    ======================================================================= */

    sortByCreatedAt(items = []) {

        return [...items].sort(

            (a, b) => {

                const aTime =

                    a.createdAt?.seconds ??

                    a.createdAt?._seconds ??

                    0;

                const bTime =

                    b.createdAt?.seconds ??

                    b.createdAt?._seconds ??

                    0;

                return bTime - aTime;

            }

        );

    }

    /* =======================================================================
       NORMALISATION COMPLETE
    ======================================================================= */

    normalizeAndSort(

        map,

        source = DATA_SOURCE.LEGACY

    ) {

        return this.sortByCreatedAt(

            this.normalizeMap(

                map,

                source

            )

        );

    }

    /* =======================================================================
    LECTURE D'UNE MAP UTILISATEUR
    ======================================================================= */

    async getUserMap(

        userId,

        fieldName,

    ) {

        const user =

            await this.getUserDocument(

                userId

            );

        if (!user) {

            return [];

        }

        return this.normalizeAndSort(

            user[fieldName] ?? {},

            DATA_SOURCE.LEGACY

        );

    }

    /* =======================================================================
       NOTIFICATIONS
    ======================================================================= */

    async getNotifications(userId) {

        return await this.getUserMap(

            userId,

            LEGACY_FIELDS.NOTIFICATIONS

        );

    }

    /* =======================================================================
       ANNONCES
    ======================================================================= */

    async getAnnouncements(userId) {

        return await this.getUserMap(

            userId,

            LEGACY_FIELDS.ANNOUNCES

        );

    }

    /* =======================================================================
       ANNONCES EFFECTUÉES
    ======================================================================= */

    async getCompletedAnnouncements(userId) {

        return await this.getUserMap(

            userId,

            LEGACY_FIELDS.COMPLETED_ANNOUNCES

        );

    }

        /* =======================================================================
       RECHERCHE DANS UNE MAP
    ======================================================================= */

    async searchInMap(

        userId,

        fieldName,

        predicate,

    ) {

        const items =

            await this.getUserMap(

                userId,

                fieldName,

            );

        if (

            typeof predicate !== "function"

        ) {

            return items;

        }

        return items.filter(

            predicate

        );

    }

    /* =======================================================================
       NOTIFICATIONS NON LUES
    ======================================================================= */

    async getUnreadNotifications(userId) {

        return await this.searchInMap(

            userId,

            "notifications",

            (notification) =>

                notification.isRead !== true

        );

    }

    /* =======================================================================
       NOMBRE DE NOTIFICATIONS NON LUES
    ======================================================================= */

    async getUnreadCount(userId) {

        const notifications =

            await this.getUnreadNotifications(

                userId

            );

        return notifications.length;

    }

    /* =======================================================================
       EXISTE-T-IL DES NOTIFICATIONS ?
    ======================================================================= */

    async hasNotifications(userId) {

        const notifications =

            await this.getNotifications(

                userId

            );

        return notifications.length > 0;

    }

        /* =======================================================================
       NOMBRE D'ELEMENTS D'UNE MAP
    ======================================================================= */

    async countItems(

        userId,

        fieldName,

    ) {

        const items =

            await this.getUserMap(

                userId,

                fieldName,

            );

        return items.length;

    }

    /* =======================================================================
       LA MAP EST-ELLE VIDE ?
    ======================================================================= */

    async isEmpty(

        userId,

        fieldName,

    ) {

        return (

            await this.countItems(

                userId,

                fieldName,

            )

        ) === 0;

    }

    /* =======================================================================
       EXISTE-T-IL AU MOINS UN ELEMENT ?
    ======================================================================= */

    async hasItems(

        userId,

        fieldName,

    ) {

        return !(

            await this.isEmpty(

                userId,

                fieldName,

            )

        );

    }

}

/* ===========================================================================
   INSTANCE UNIQUE
=========================================================================== */

const legacyRepository =

    new LegacyRepository();

export default legacyRepository;